/**
 * One bucket-group card with its buckets (FE-3, PHASE1 §7). Presentational:
 * receives the (already ordered, visibility-filtered) group and raises callbacks
 * for edit/add/archive/reorder. The System group and the Transfer/system bucket
 * cannot be archived or reordered.
 */
import { ArrowDown, ArrowUp, Lock, Pencil, Plus } from 'lucide-react';
import { Button } from '../../components/ui/Button.tsx';
import type { BucketGroupWithBuckets, BucketOut } from '../../lib/types.ts';

const SYSTEM_GROUP_SLUG = 'system';

/** Row action icon recipe (DESIGN.md §3.12). */
const ICON_BTN =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors duration-150 hover:bg-slate-50 hover:text-slate-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 disabled:opacity-30 dark:text-slate-500 dark:hover:bg-slate-800/60 dark:hover:text-slate-300 dark:focus-visible:outline-sky-500';

/** Pastel gray pill (DESIGN.md §3.11). */
const GRAY_BADGE =
  'inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300';

export interface BucketGroupSectionProps {
  group: BucketGroupWithBuckets;
  isFirst: boolean;
  isLast: boolean;
  reordering: boolean;
  onMoveGroup: (dir: -1 | 1) => void;
  onEditGroup: () => void;
  onArchiveGroup: () => void;
  onAddBucket: () => void;
  onEditBucket: (bucket: BucketOut) => void;
  onArchiveBucket: (bucket: BucketOut) => void;
  onReorderBuckets: (orderedIds: number[]) => void;
}

export function BucketGroupSection({
  group,
  isFirst,
  isLast,
  reordering,
  onMoveGroup,
  onEditGroup,
  onArchiveGroup,
  onAddBucket,
  onEditBucket,
  onArchiveBucket,
  onReorderBuckets,
}: BucketGroupSectionProps) {
  const isSystemGroup = group.slug === SYSTEM_GROUP_SLUG;
  const buckets = group.buckets;

  function moveBucket(index: number, dir: -1 | 1) {
    const next = [...buckets];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    onReorderBuckets(next.map((b) => b.id));
  }

  return (
    <section>
      <header className="flex items-center gap-2 border-b border-slate-100 pb-2 dark:border-slate-800/70">
        {group.color ? (
          <span
            aria-hidden
            className="h-3 w-3 shrink-0 rounded-full"
            style={{ backgroundColor: group.color }}
          />
        ) : null}
        {group.icon ? <span aria-hidden>{group.icon}</span> : null}
        <h2 className="min-w-0 flex-1 truncate text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
          {group.name}
        </h2>
        {group.archived ? <span className={GRAY_BADGE}>Archiviert</span> : null}

        {!isSystemGroup ? (
          <>
            <button
              type="button"
              onClick={() => onMoveGroup(-1)}
              disabled={isFirst || reordering}
              aria-label="Gruppe nach oben"
              className={ICON_BTN}
            >
              <ArrowUp className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => onMoveGroup(1)}
              disabled={isLast || reordering}
              aria-label="Gruppe nach unten"
              className={ICON_BTN}
            >
              <ArrowDown className="h-4 w-4" />
            </button>
          </>
        ) : (
          <span
            className="inline-flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500"
            title="System-Gruppe"
          >
            <Lock className="h-3.5 w-3.5" />
          </span>
        )}
        <button
          type="button"
          onClick={onEditGroup}
          aria-label="Gruppe bearbeiten"
          className={ICON_BTN}
        >
          <Pencil className="h-4 w-4" />
        </button>
      </header>

      <ul className="divide-y divide-slate-100 dark:divide-slate-800/70">
        {buckets.length === 0 ? (
          <li className="py-3 text-sm text-slate-400 dark:text-slate-500">Keine Kategorien in dieser Gruppe.</li>
        ) : (
          buckets.map((bucket, index) => {
            const locked = bucket.is_system;
            return (
              <li
                key={bucket.id}
                className="-mx-2 flex items-center gap-2 rounded px-2 py-3 transition-colors duration-150 hover:bg-slate-50 dark:hover:bg-slate-800/60"
              >
                {bucket.color ? (
                  <span
                    aria-hidden
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: bucket.color }}
                  />
                ) : null}
                {bucket.icon ? <span aria-hidden>{bucket.icon}</span> : null}
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                  {bucket.name}
                </span>
                {bucket.excluded_from_reports ? (
                  <span className={GRAY_BADGE}>ohne Auswertung</span>
                ) : null}
                {bucket.archived ? <span className={GRAY_BADGE}>archiviert</span> : null}

                {!locked ? (
                  <>
                    <button
                      type="button"
                      onClick={() => moveBucket(index, -1)}
                      disabled={index === 0 || reordering}
                      aria-label="Nach oben"
                      className={ICON_BTN}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveBucket(index, 1)}
                      disabled={index === buckets.length - 1 || reordering}
                      aria-label="Nach unten"
                      className={ICON_BTN}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <Lock className="h-3.5 w-3.5 text-slate-300 dark:text-slate-600" aria-label="System-Kategorie" />
                )}
                <button
                  type="button"
                  onClick={() => onEditBucket(bucket)}
                  aria-label="Kategorie bearbeiten"
                  className={ICON_BTN}
                >
                  <Pencil className="h-4 w-4" />
                </button>
                {!locked ? (
                  <Button variant="ghost" size="sm" onClick={() => onArchiveBucket(bucket)}>
                    {bucket.archived ? 'Aktiv' : 'Archiv'}
                  </Button>
                ) : null}
              </li>
            );
          })
        )}
      </ul>

      <footer className="flex items-center justify-between gap-2 pt-1">
        <Button variant="ghost" size="sm" onClick={onAddBucket}>
          <Plus className="h-4 w-4" />
          Kategorie
        </Button>
        {!isSystemGroup ? (
          <Button variant="ghost" size="sm" onClick={onArchiveGroup}>
            {group.archived ? 'Gruppe reaktivieren' : 'Gruppe archivieren'}
          </Button>
        ) : null}
      </footer>
    </section>
  );
}
